import React, { ReactElement } from 'react';
import Image from 'next/image';
import { Disclosure } from '@headlessui/react';
import { MenuIcon, XIcon } from '@heroicons/react/outline';
import { useRequireAuth } from 'lib/auth';
import speakerNotesBlack from 'public/speaker_notes_black_48dp.svg';
import { NavBarMenu, NavBarMenuMobile } from 'components/nav-bar-menu';
import UserProfileMenu from 'components/user/user-profile-menu';
import { UserCircleIcon } from '@heroicons/react/solid';
import { MenuLink } from 'components/menu-link';
import { useGoogleUserIdFromParameter } from 'lib/useGoogleUserIdFromParameter';

export default function NavBar(): ReactElement {
  const auth = useRequireAuth();
  const googleUserId = useGoogleUserIdFromParameter();

  return (
    <Disclosure as="nav">
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Image
                    className="h-8 w-auto sm:h-10"
                    src={speakerNotesBlack}
                    alt=""
                    layout="fixed"
                    quality={100}
                    priority
                  />
                </div>
                <div className="ml-4 text-2xl font-bold">TranscribeKit</div>
                <div className="hidden md:block">
                  <NavBarMenu />
                </div>
              </div>
              <div className="hidden md:block">
                <div className="ml-4 flex items-center md:ml-6">
                  {auth?.state.user ? (
                    <UserProfileMenu auth={auth} />
                  ) : (
                    <div className="h-8 w-8 rounded-full" />
                  )}
                </div>
              </div>
              <div className="-mr-2 flex md:hidden">
                {/* Mobile menu button */}
                <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="md:hidden border-b">
            <NavBarMenuMobile />
            <div className="pt-4 pb-3">
              <div className="flex items-center px-5">
                <div className="flex-none">
                  {auth?.state.user?.photoURL ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      className="h-10 w-10 rounded-full"
                      src={auth.state.user.photoURL}
                      alt=""
                    />
                  ) : (
                    <UserCircleIcon className="h-10 w-10" />
                  )}
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <div className="pb-1 truncate text-base font-medium leading-none">
                    {auth?.state.user?.displayName}
                  </div>
                  <div className="pb-1 truncate text-sm font-medium leading-none text-gray-500">
                    {auth?.state.user?.email}
                  </div>
                </div>
              </div>
              <div className="mt-3 px-2 space-y-1">
                <MenuLink
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  href="/profile"
                >
                  Settings
                </MenuLink>
                {!googleUserId && (
                  <a
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    onClick={() => auth?.switchAccounts()}
                  >
                    Switch user
                  </a>
                )}
                <a
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  onClick={() => auth?.signOut()}
                >
                  Sign out
                </a>
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
